import { useState, useEffect } from 'react';
import { MapPin } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface LocationPickerProps {
  onLocationSelect: (venue: string, lat: number, lng: number) => void;
  defaultVenue?: string;
}

const LocationPicker = ({ onLocationSelect, defaultVenue = '' }: LocationPickerProps) => {
  const [venue, setVenue] = useState(defaultVenue);
  const [suggestions, setSuggestions] = useState<Array<{
    place_name: string;
    center: [number, number];
  }>>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    setVenue(defaultVenue);
  }, [defaultVenue]);

  const { data: mapboxToken } = useQuery({
    queryKey: ['mapbox-token'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('get-mapbox-token');
      if (error) throw error;
      return data.token;
    },
  });

  const searchLocation = async (query: string) => {
    if (!query || !mapboxToken) return;
    
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          query
        )}.json?access_token=${mapboxToken}&types=address,poi`
      );
      
      if (!response.ok) throw new Error('Failed to fetch location data');
      
      const data = await response.json();
      setSuggestions(data.features);
      setShowSuggestions(true);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch location suggestions",
        variant: "destructive",
      });
    }
  };

  const handleSuggestionClick = (suggestion: {
    place_name: string;
    center: [number, number];
  }) => {
    setVenue(suggestion.place_name);
    onLocationSelect(suggestion.place_name, suggestion.center[1], suggestion.center[0]);
    setShowSuggestions(false);
  };

  return (
    <div className="space-y-2 relative">
      <Label htmlFor="venue">Venue</Label>
      <div className="relative">
        <Input
          id="venue"
          value={venue}
          onChange={(e) => {
            setVenue(e.target.value);
            searchLocation(e.target.value);
          }}
          className="pr-10"
          placeholder="Search for a venue..."
        />
        <MapPin className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
      </div>
      
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-10 w-full bg-white border rounded-md shadow-lg mt-1">
          {suggestions.map((suggestion, index) => (
            <Button
              key={index}
              variant="ghost"
              className="w-full justify-start text-left px-4 py-2 hover:bg-gray-100"
              onClick={() => handleSuggestionClick(suggestion)}
            >
              {suggestion.place_name}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocationPicker;